import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ApplicationDocumentUploadRequestBody,
  deleteApplicationDocument,
  getApplicationDocumentDownloadUrl,
  listApplicationDocuments,
  uploadApplicationDocument,
} from '@access-cms/api-client';
import {
  DOCUMENT_UPLOAD_ACCEPTED_FORMATS,
  MAX_DOCUMENT_UPLOAD_MB,
  mimeForFilename,
} from '@access-cms/shared-constants';

import { Accordion, AccordionItem } from '../../../../components/Accordion';
import { Drawer } from '../../../../components/Drawer';
import { FileUpload, FileUploadError, UploadedFile } from '../../../../components/FileUpload';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { ErrorIcon } from '../../../../components/Icon';
import { normalizeTestId } from '../../../../utils';
import { PrimaryStep, SubStep, WorkflowSubStepProps } from '../../config';
import { useApplication, WorkflowAlertVariation } from '../../context';

import './DocumentUploadPage.css';

export interface DocumentUploadFormData {
  pivTrainingCertificate: UploadedFile[];
  resume: UploadedFile[];
  passport: UploadedFile[];
  usVisaDocument: UploadedFile[];
  residencyDocument: UploadedFile[];
  residencySupportingDocuments: UploadedFile[];
  legalDocuments: UploadedFile[];
  otherDocuments: UploadedFile[];
}

type DocumentField = keyof DocumentUploadFormData;
type ApplicationDocumentListItem = NonNullable<Awaited<ReturnType<typeof listApplicationDocuments>>['data']>[number];

interface DocumentConfig {
  field: DocumentField;
  title: string;
  label: string;
  required: boolean;
  documentType: ApplicationDocumentUploadRequestBody['documentType'];
  acceptedFormats: string[];
  maxSizeMB: number;
  helpText: string;
  /** Optional link text within the help text */
  linkText?: string;
  /** Optional link URL */
  linkUrl?: string;
  /** Optional text to show before the FileUpload (e.g., "You may upload one document.") */
  uploadNote?: string;
  /** Optional applicant branch that can see this document type. */
  applicantBranch?: 'nonUSCitizen' | 'permanentResident' | 'nonPermanentResident';
  /** If true, more than one file may be associated to this document field */
  allowMultiple?: boolean;
  /** Optional backend label used to distinguish OTHER document collections. */
  documentLabel?: string;
  /** Config participates in hydration/upload but is rendered in a combined section. */
  combinedSection?: 'legalOther' | 'residencySupport';
}

const MAX_GROUP_DOCUMENTS = 10;
const RESIDENCY_SUPPORTING_DOCUMENT_LABEL = 'RESIDENCY_SUPPORTING_DOCUMENT';

/**
 * ACP-2145: accepted formats and the size cap come from the shared upload policy
 * so the widget, the API schema and the service can no longer drift apart.
 */
const ACCEPTED_UPLOAD_FORMATS = [...DOCUMENT_UPLOAD_ACCEPTED_FORMATS];

const documentConfigs: DocumentConfig[] = [
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    documentType: 'PIV_TRAINING',
    field: 'pivTrainingCertificate',
    helpText:
      'The PIV Applicant Training (PPTX) is required for all contractors who need a Personal Identity Verification (PIV) card. Only upload the signed Certificate of Completion, which can be found on the last page of the training.',
    label: 'Upload PIV Training Certificate',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: true,
    title: 'PIV Training Certificate',
    uploadNote: 'You may upload one document.',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    documentType: 'RESUME',
    field: 'resume',
    helpText:
      'Upload your current resume or curriculum vitae (CV). Be sure to include your work history, education, and any relevant skills.',
    label: 'Upload Resume',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: true,
    title: 'Resume',
    uploadNote: 'You may upload one document.',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    applicantBranch: 'nonUSCitizen',
    documentType: 'PASSPORT',
    field: 'passport',
    helpText:
      "You must upload this document because you said you're not a United States citizen. You must have a current passport. Upload a clear copy of the photo page of your valid passport. Ensure all text is legible. You do not need to upload entry- and exit-stamped pages.",
    label: 'Upload Passport',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: true,
    title: 'Passport',
    uploadNote: 'You may upload one document.',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    applicantBranch: 'nonPermanentResident',
    documentType: 'VISA',
    field: 'usVisaDocument',
    helpText:
      "Since you're not a U.S. citizen, you must upload proof that you're allowed to live and work in the United States. You may upload your current Visa. If your Visa is expired, please provide your most recent Visa and any Visa extension documentation, such as Form I-94 or Form I-797A.",
    label: 'Upload required U.S. visa or U.S. Permanent Resident card',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: true,
    title: 'Residency Documents',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    applicantBranch: 'permanentResident',
    documentType: 'RESIDENCY',
    field: 'residencyDocument',
    helpText:
      "Since you're not a U.S. citizen, you must upload proof that you're allowed to live and work in the United States. You may upload your Permanent Resident Card (Green Card). If your Permanent Resident Card is expired, please provide your most recent card and any extension documentation.",
    label: 'Upload required U.S. visa or U.S. Permanent Resident card',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: true,
    title: 'Residency Documents',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    allowMultiple: true,
    applicantBranch: 'nonUSCitizen',
    combinedSection: 'residencySupport',
    documentLabel: RESIDENCY_SUPPORTING_DOCUMENT_LABEL,
    documentType: 'OTHER',
    field: 'residencySupportingDocuments',
    helpText: '',
    label: 'Upload any other U.S. residency documents you may have',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: false,
    title: 'Residency Supporting Documents',
  },
  // Legacy LEGAL records remain hydratable and removable, but this config is
  // not rendered as an upload target. All new combined-section files use OTHER.
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    allowMultiple: true,
    combinedSection: 'legalOther',
    documentType: 'LEGAL',
    field: 'legalDocuments',
    helpText:
      'Upload any relevant legal documents such as name change documentation, court orders, or other legal paperwork.',
    label: 'Upload Legal Documents',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: false,
    title: 'Legal Documents',
  },
  {
    acceptedFormats: ACCEPTED_UPLOAD_FORMATS,
    allowMultiple: true,
    combinedSection: 'legalOther',
    documentType: 'OTHER',
    field: 'otherDocuments',
    helpText:
      'Upload any other files you may need for your application. These may be name-change documents, court orders, or other legal papers.',
    label: 'Upload Other Documents',
    maxSizeMB: MAX_DOCUMENT_UPLOAD_MB,
    required: false,
    title: 'Legal and Other Documents',
  },
];

/**
 * Resolve the MIME type to declare for an upload.
 *
 * ACP-2145: the API now requires the declared type to match the filename
 * extension, and signs the presigned URL for exactly that type. So the extension
 * is the authority here — `file.type` is browser-reported, can be empty, and can
 * disagree with the name, which would turn into a 400 at reservation or a 403
 * from S3 on the PUT.
 */
function resolveSupportedMimeType(file: File): ApplicationDocumentUploadRequestBody['mimeType'] {
  const mimeType = mimeForFilename(file.name);

  if (!mimeType) {
    throw new Error(
      'This file format is not supported by document upload. Please upload a PDF, JPG, JPEG, or PNG file.',
    );
  }

  return mimeType;
}

function isScanInProgress(scanStatus?: string): boolean {
  return scanStatus === 'PENDING' || scanStatus === 'SCANNING';
}

function hasFileError(file: UploadedFile): boolean {
  return Boolean(file.error) || file.scanStatus === 'INFECTED' || file.scanStatus === 'ERROR';
}

function getDocumentField(document: { documentType?: string; label?: string | null }): DocumentField | undefined {
  if (document.documentType === 'OTHER' && document.label === RESIDENCY_SUPPORTING_DOCUMENT_LABEL) {
    return 'residencySupportingDocuments';
  }

  return documentConfigs.find(
    (config) => config.documentType === document.documentType && config.combinedSection !== 'residencySupport',
  )?.field;
}

function DocumentUploadPage({
  data,
  updateData,
  allFormData,
  fieldErrors,
}: WorkflowSubStepProps<DocumentUploadFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'document-upload-page';
  const { applicationId, showWorkflowAlert, dismissWorkflowAlert } = useApplication();
  const latestDataRef = useRef(data);

  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  const updateFieldFiles = useCallback(
    (field: DocumentField, updater: (files: UploadedFile[]) => UploadedFile[]) => {
      const currentData = latestDataRef.current;
      const nextData = {
        ...currentData,
        [field]: updater(currentData[field] || []),
      };
      latestDataRef.current = nextData;
      updateData(nextData);
    },
    [updateData],
  );

  const updateFileFromDocument = useCallback(
    (document: ApplicationDocumentListItem) => {
      const field = getDocumentField(document);

      if (!field) return;

      updateFieldFiles(field, (currentFiles) => {
        let hasChanges = false;
        const nextFiles = currentFiles.map((currentFile) => {
          const documentId = currentFile.documentId ?? currentFile.id;

          if (documentId !== document.id) return currentFile;

          const nextFile = {
            ...currentFile,
            documentId: document.id,
            documentLabel: document.label ?? undefined,
            documentType: document.documentType,
            mimeType: document.mimeType ?? currentFile.mimeType,
            name: document.filename ?? currentFile.name,
            scanStatus: document.scanStatus ?? currentFile.scanStatus,
            uploadedAt: document.updatedAt ?? currentFile.uploadedAt,
            uploadStatus: 'associated' as const,
          };

          hasChanges =
            currentFile.scanStatus !== nextFile.scanStatus ||
            currentFile.mimeType !== nextFile.mimeType ||
            currentFile.name !== nextFile.name ||
            currentFile.uploadedAt !== nextFile.uploadedAt ||
            currentFile.uploadStatus !== nextFile.uploadStatus;

          return nextFile;
        });

        return hasChanges ? nextFiles : currentFiles;
      });
    },
    [updateFieldFiles],
  );

  const hydrateDocuments = useCallback(
    (documents: ApplicationDocumentListItem[]) => {
      const documentsByField = new Map<
        DocumentField,
        Array<ApplicationDocumentListItem & { documentType: string; filename: string; id: string }>
      >();

      documents.forEach((document) => {
        if (!document.id || !document.documentType || !document.filename) return;
        // PENDING is only the backend reservation state. A confirmed upload
        // transitions immediately to SCANNING or CLEAN, so reservations must
        // not be restored as associated files in the application form.
        if (document.scanStatus === 'PENDING') return;
        const field = getDocumentField(document);
        if (!field) return;
        const current = documentsByField.get(field) ?? [];
        documentsByField.set(field, [...current, document as (typeof current)[number]]);
      });

      documentConfigs.forEach((config) => {
        const serverFiles = (documentsByField.get(config.field) ?? []).map((document) => ({
          documentId: document.id,
          documentLabel: document.label ?? undefined,
          documentType: document.documentType,
          id: document.id,
          isExisting: true,
          mimeType: document.mimeType,
          name: document.filename,
          scanStatus: document.scanStatus,
          size: document.sizeBytes ?? 0,
          uploadedAt: document.updatedAt ?? undefined,
          uploadStatus: 'associated' as const,
        }));

        updateFieldFiles(config.field, (currentFiles) => {
          const transientFiles = currentFiles.filter((file) => !file.documentId || file.isUploading);
          const nextFiles = config.allowMultiple
            ? [...serverFiles, ...transientFiles]
            : transientFiles.length > 0
            ? transientFiles
            : serverFiles.slice(0, 1);

          return nextFiles;
        });
      });
    },
    [updateFieldFiles],
  );

  const loadCurrentDocuments = useCallback(async () => {
    if (!applicationId) return;

    try {
      const response = await listApplicationDocuments(applicationId);
      hydrateDocuments(response.data ?? []);
    } catch (error) {
      console.error('[ApplicationDocument] Failed to load current documents', {
        applicationId,
        error: error instanceof Error ? error.message : String(error),
      });
      showWorkflowAlert({
        heading: 'Documents Could Not Be Loaded',
        text: 'Your current document statuses could not be loaded. Please refresh and try again.',
        variation: WorkflowAlertVariation.ERROR,
      });
    }
  }, [applicationId, hydrateDocuments, showWorkflowAlert]);

  useEffect(() => {
    void loadCurrentDocuments();
  }, [applicationId, loadCurrentDocuments]);

  const refreshScanningDocuments = useCallback(async () => {
    if (!applicationId) return;

    const files = Object.values(latestDataRef.current).flat();
    const hasDocumentsToRefresh = files.some(
      (file) => file.documentId && (!file.scanStatus || isScanInProgress(file.scanStatus)),
    );

    if (!hasDocumentsToRefresh) return;

    try {
      const response = await listApplicationDocuments(applicationId);
      const documents = response.data ?? [];
      const documentsById = new Map(documents.map((document) => [document.id, document]));

      files.forEach((file) => {
        const documentId = file.documentId ?? file.id;
        const document = documentsById.get(documentId);

        if (document && document.scanStatus !== file.scanStatus) {
          updateFileFromDocument(document);
        } else if (!document && file.documentId) {
          const field = (Object.keys(latestDataRef.current) as DocumentField[]).find((candidateField) =>
            latestDataRef.current[candidateField]?.some(
              (currentFile) => (currentFile.documentId ?? currentFile.id) === documentId,
            ),
          );
          if (field) {
            updateFieldFiles(field, (currentFiles) =>
              currentFiles.filter((currentFile) => (currentFile.documentId ?? currentFile.id) !== documentId),
            );
          }
        }
      });
    } catch (error) {
      console.error('[ApplicationDocument] Failed to refresh document scan status', {
        applicationId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [applicationId, updateFileFromDocument, updateFieldFiles]);

  const scanStatusWatchKey = useMemo(() => {
    return Object.values(data)
      .flat()
      .filter((file) => file.documentId && (!file.scanStatus || isScanInProgress(file.scanStatus)))
      .map((file) => file.documentId ?? file.id)
      .sort()
      .join('|');
  }, [data]);

  useEffect(() => {
    if (!scanStatusWatchKey) return;

    void refreshScanningDocuments();

    const intervalId = window.setInterval(() => {
      void refreshScanningDocuments();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [refreshScanningDocuments, scanStatusWatchKey]);

  // Check if user is a non-U.S. citizen based on their citizenship status answer
  const isNonUSCitizen = useMemo(() => {
    const citizenshipData = allFormData?.[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
      | { isUSCitizen?: boolean | null }
      | undefined;

    return citizenshipData?.isUSCitizen === false;
  }, [allFormData]);

  const isPermanentResident = useMemo(() => {
    const residencyData = allFormData?.[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
      | { isPermanentResident?: boolean | null }
      | undefined;

    return isNonUSCitizen && residencyData?.isPermanentResident === true;
  }, [allFormData, isNonUSCitizen]);

  const isNonPermanentResident = useMemo(() => {
    const residencyData = allFormData?.[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
      | { isPermanentResident?: boolean | null }
      | undefined;

    return isNonUSCitizen && residencyData?.isPermanentResident === false;
  }, [allFormData, isNonUSCitizen]);

  // Filter document configs based on citizenship status
  const filteredDocumentConfigs = useMemo(() => {
    return documentConfigs.filter((config) => {
      if (config.combinedSection) return false;
      if (config.applicantBranch === 'nonUSCitizen') return isNonUSCitizen;
      if (config.applicantBranch === 'permanentResident') return isPermanentResident;
      if (config.applicantBranch === 'nonPermanentResident') return isNonPermanentResident;

      return true;
    });
  }, [isNonPermanentResident, isNonUSCitizen, isPermanentResident]);

  const handleFilesChange = useCallback(
    (config: DocumentConfig) => (files: UploadedFile[]) => {
      const uploadCandidates = files.filter(
        (file) =>
          !file.error && !file.documentId && file.uploadStatus !== 'uploading' && file.uploadStatus !== 'failed',
      );
      const filesWithUploadState = files.map((file) =>
        uploadCandidates.some((candidate) => candidate.id === file.id)
          ? {
              ...file,
              documentLabel: config.documentLabel,
              documentType: config.documentType,
              isUploading: true,
              uploadStatus: 'uploading' as const,
            }
          : file,
      );

      updateFieldFiles(config.field, () => filesWithUploadState);

      uploadCandidates.forEach((uploadedFile) => {
        const file = uploadedFile.file;

        const failUpload = (message: string) => {
          console.error('[ApplicationDocument] Failed to associate selected file', {
            applicationId,
            documentType: config.documentType,
            error: message,
            filename: uploadedFile.name,
          });
          updateFieldFiles(config.field, (currentFiles) =>
            currentFiles.map((currentFile) =>
              currentFile.id === uploadedFile.id
                ? {
                    ...currentFile,
                    error: message,
                    isUploading: false,
                    uploadStatus: 'failed',
                  }
                : currentFile,
            ),
          );
          showWorkflowAlert({
            heading: 'Document Upload Failed',
            text: message,
            variation: WorkflowAlertVariation.ERROR,
          });
        };

        if (!applicationId) {
          failUpload('Your application must be saved before documents can be uploaded. Please try again.');

          return;
        }

        if (!file) {
          failUpload('The selected file could not be read. Please remove it and choose the file again.');

          return;
        }

        let mimeType: ApplicationDocumentUploadRequestBody['mimeType'];
        try {
          mimeType = resolveSupportedMimeType(file);
        } catch (error) {
          failUpload(error instanceof Error ? error.message : String(error));

          return;
        }

        void uploadApplicationDocument({
          applicationId,
          body: {
            documentType: config.documentType,
            filename: uploadedFile.name,
            ...(config.documentType === 'OTHER' ? { label: config.documentLabel ?? uploadedFile.name } : {}),
            mimeType,
            size: uploadedFile.size,
          },
          file,
        })
          .then((result) => {
            updateFieldFiles(config.field, (currentFiles) =>
              currentFiles.map((currentFile) =>
                currentFile.id === uploadedFile.id
                  ? {
                      ...currentFile,
                      documentId: result.documentId,
                      documentLabel: result.documentLabel,
                      documentType: result.documentType,
                      error: undefined,
                      id: result.documentId,
                      isUploading: false,
                      mimeType: result.mimeType,
                      name: result.filename,
                      scanStatus: result.scanStatus,
                      size: result.sizeBytes,
                      uploadedAt: result.uploadedAt,
                      uploadStatus: 'associated',
                    }
                  : currentFile,
              ),
            );
          })
          .catch((error: Error) => {
            failUpload(error.message || 'The document could not be uploaded. Please try again.');
          });
      });
    },
    [applicationId, showWorkflowAlert, updateFieldFiles],
  );

  const handleFileError = useCallback(
    (error: FileUploadError) => {
      const heading =
        error.reason === 'size'
          ? 'File Size Error'
          : error.reason === 'count'
          ? 'File Limit Reached'
          : 'File Type Error';
      showWorkflowAlert({
        heading,
        text: error.message,
        variation: WorkflowAlertVariation.ERROR,
      });
    },
    [showWorkflowAlert],
  );

  const handleFileRemove = useCallback(
    (removedFile: UploadedFile) => {
      // Dismiss the workflow alert when a file with an error is removed
      if (removedFile.error) {
        dismissWorkflowAlert();
      }

      const documentId = removedFile.documentId ?? removedFile.id;
      if (!applicationId || !documentId || removedFile.uploadStatus !== 'associated') {
        return;
      }

      void deleteApplicationDocument(applicationId, documentId).catch((error: unknown) => {
        console.error('[ApplicationDocument] Failed to delete removed document', {
          applicationId,
          documentId,
          error: error instanceof Error ? error.message : String(error),
          filename: removedFile.name,
        });
        showWorkflowAlert({
          heading: 'Document Remove Failed',
          text: 'The document could not be removed. Please try again.',
          variation: WorkflowAlertVariation.ERROR,
        });
      });
    },
    [applicationId, dismissWorkflowAlert, showWorkflowAlert],
  );

  const handleFileClick = useCallback(
    async (file: UploadedFile) => {
      const documentId = file.documentId ?? file.id;

      if (!applicationId || !documentId) {
        showWorkflowAlert({
          heading: 'Document Not Available',
          text: 'This document is not available yet. Please wait for upload to complete and try again.',
          variation: WorkflowAlertVariation.ERROR,
        });

        return;
      }

      const documentWindow = window.open('about:blank', '_blank');
      if (documentWindow) {
        documentWindow.opener = null;
      }

      try {
        const response = await getApplicationDocumentDownloadUrl(applicationId, documentId);
        const downloadUrl = response.data?.downloadUrl;

        if (!downloadUrl) {
          throw new Error('Document download URL was not returned.');
        }

        if (documentWindow) {
          documentWindow.location.href = downloadUrl;
        } else {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        documentWindow?.close();
        console.error('[ApplicationDocument] Failed to open uploaded document', {
          applicationId,
          documentId,
          error: error instanceof Error ? error.message : String(error),
          filename: file.name,
        });
        showWorkflowAlert({
          heading: 'Document Not Available',
          text: 'The document could not be opened. Please make sure the malware scan is complete and try again.',
          variation: WorkflowAlertVariation.ERROR,
        });
      }
    },
    [applicationId, showWorkflowAlert],
  );

  const accordionItems: AccordionItem[] = useMemo(() => {
    const residencySupportConfig = documentConfigs.find((config) => config.field === 'residencySupportingDocuments')!;
    const otherConfig = documentConfigs.find((config) => config.field === 'otherDocuments')!;

    const items: AccordionItem[] = filteredDocumentConfigs.map((config) => {
      const itemTestId = normalizeTestId(`${baseTestId}-${config.field}`);
      const isResidencyDocuments = config.field === 'usVisaDocument' || config.field === 'residencyDocument';
      const sectionFiles = isResidencyDocuments
        ? [...(data[config.field] || []), ...(data.residencySupportingDocuments || [])]
        : data[config.field] || [];
      const hasSectionFileError = sectionFiles.some(hasFileError);

      const helpContent =
        config.field === 'pivTrainingCertificate' ? (
          <p className='document-upload-help-text'>
            The{' '}
            <a
              className='document-upload-link'
              href='#piv-training'>
              PIV Applicant Training (PPTX)
            </a>{' '}
            is required for all contractors who need a Personal Identity Verification (PIV) card. Only upload the signed
            Certificate of Completion, which can be found on the last page of the training.
          </p>
        ) : config.field === 'passport' ? (
          <>
            <p className='document-upload-help-text'>
              You must upload this document because you said you&apos;re not a United States citizen.
            </p>
            <p className='document-upload-help-text ds-u-margin-top--1'>
              You must have a current passport. Upload a clear copy of the photo page of your valid passport. Ensure all
              text is legible. You do not need to upload entry- and exit-stamped pages.
            </p>
          </>
        ) : isResidencyDocuments ? (
          <>
            <p className='document-upload-help-text'>
              You must upload this document because you said you&apos;re not a United States citizen.
            </p>
            <p className='document-upload-help-text ds-u-margin-top--1'>
              Upload a copy of your U.S. visa or U.S. Permanent Resident card.
            </p>
            <p className='document-upload-help-text ds-u-margin-top--1'>
              If your card has expired, please provide a current Form I-94, Form I-797A, and/or Form I-20 that specifies
              how long the expired card is extended, in addition to your expired visa or U.S. Permanent Resident card.
            </p>
            <p className='document-upload-help-text ds-u-margin-top--1'>
              Canadian and Mexican citizens may instead upload the TN (temporary non-immigrant) visa.
            </p>
            <p className='document-upload-help-text ds-u-margin-top--1'>
              Employment authorization cards (EAD), Social Security cards, or driver&apos;s licenses are not accepted.
              You may upload more than one document.
            </p>
          </>
        ) : (
          <p className='document-upload-help-text'>{config.helpText}</p>
        );

      return {
        content: (
          <>
            <div
              className='document-upload-text'
              data-testid={`${itemTestId}-text`}>
              {helpContent}
              {!isResidencyDocuments && <p className='document-upload-note'>{config.uploadNote}</p>}
            </div>
            <FileUpload
              acceptedFormats={config.acceptedFormats}
              data-testid={`${itemTestId}-upload`}
              errorMessage={fieldErrors?.[config.field]}
              files={data[config.field] || []}
              label={config.label}
              maxFiles={config.allowMultiple ? undefined : 1}
              maxSizeMB={config.maxSizeMB}
              required={isResidencyDocuments}
              onFileClick={handleFileClick}
              onFileError={handleFileError}
              onFileRemove={handleFileRemove}
              onFilesChange={handleFilesChange(config)}
            />
            {isResidencyDocuments && (
              <div className='document-upload-secondary-uploader'>
                <FileUpload
                  acceptedFormats={residencySupportConfig.acceptedFormats}
                  data-testid={`${itemTestId}-supporting-upload`}
                  files={data.residencySupportingDocuments || []}
                  label={residencySupportConfig.label}
                  maxFiles={MAX_GROUP_DOCUMENTS - 1}
                  maxSizeMB={residencySupportConfig.maxSizeMB}
                  onFileClick={handleFileClick}
                  onFileError={handleFileError}
                  onFileRemove={handleFileRemove}
                  onFilesChange={handleFilesChange(residencySupportConfig)}
                />
              </div>
            )}
          </>
        ),
        icon: hasSectionFileError ? <ErrorIcon size={32} /> : undefined,
        id: config.field,
        required: config.required,
        title: config.title,
      };
    });

    const legalOtherFiles = [...(data.legalDocuments || []), ...(data.otherDocuments || [])];
    const handleLegalOtherFilesChange = (files: UploadedFile[]) => {
      const legacyLegalFiles = files.filter((file) => file.documentType === 'LEGAL');
      const otherFiles = files.filter((file) => file.documentType !== 'LEGAL');

      updateFieldFiles('legalDocuments', () => legacyLegalFiles);
      handleFilesChange(otherConfig)(otherFiles);
    };

    items.push({
      content: (
        <>
          <div className='document-upload-text'>
            <p className='document-upload-help-text'>
              Upload any other files you may need for your application. These may be name-change documents, court
              orders, or other legal papers.
            </p>
            <p className='document-upload-note'>You may upload more than one document.</p>
          </div>
          <FileUpload
            acceptedFormats={otherConfig.acceptedFormats}
            ariaLabel='Legal and Other Documents'
            data-testid='document-upload-legal-other-upload'
            files={legalOtherFiles}
            label=''
            maxFiles={MAX_GROUP_DOCUMENTS}
            maxSizeMB={otherConfig.maxSizeMB}
            onFileClick={handleFileClick}
            onFileError={handleFileError}
            onFileRemove={handleFileRemove}
            onFilesChange={handleLegalOtherFilesChange}
          />
        </>
      ),
      icon: legalOtherFiles.some(hasFileError) ? <ErrorIcon size={32} /> : undefined,
      id: 'legal-and-other-documents',
      required: false,
      title: 'Legal and Other Documents',
    });

    return items;
  }, [
    data,
    fieldErrors,
    filteredDocumentConfigs,
    handleFilesChange,
    handleFileClick,
    handleFileError,
    handleFileRemove,
    updateFieldFiles,
  ]);

  // Calculate which accordion items should be open by default (those with files attached)
  const defaultOpenItems = useMemo(() => {
    const openItems: string[] = filteredDocumentConfigs
      .filter((config) => {
        const files =
          config.field === 'usVisaDocument' || config.field === 'residencyDocument'
            ? [...(data[config.field] || []), ...(data.residencySupportingDocuments || [])]
            : data[config.field];

        return files && files.length > 0;
      })
      .map((config) => config.field);
    if ((data.legalDocuments?.length ?? 0) + (data.otherDocuments?.length ?? 0) > 0) {
      openItems.push('legal-and-other-documents');
    }

    return openItems;
  }, [data, filteredDocumentConfigs]);

  return (
    <div data-testid={baseTestId}>
      <p
        className='document-upload-intro'
        data-testid={`${baseTestId}-intro`}>
        Upload the following documents to support your application:
      </p>

      <Accordion
        allowMultipleOpen
        data-testid={`${baseTestId}-accordion`}
        defaultOpenItems={defaultOpenItems}
        forceOpenItems={Object.keys(fieldErrors ?? {}).filter((field) => fieldErrors?.[field])}
        items={accordionItems}
      />

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Document Upload'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Document Upload</h3>
          <p>
            Please upload the documents you need to finish your application. Each type of document has certain format
            and size rules.
          </p>

          <h4 className='ds-u-margin-top--3'>Required Documents</h4>
          <ul>
            <li>
              <strong>PIV Training Certificate:</strong> Only upload the signed Certificate of Completion. This is found
              on the last page of the training.
            </li>
            <li>
              <strong>Resume:</strong> Upload your current resume or curriculum vitae (CV). Be sure to include your work
              history, education, and any relevant skills.
            </li>
            {isNonUSCitizen && (
              <>
                <li>
                  <strong>Passport:</strong> You must upload this document because you said you're not a United States
                  citizen. You must have a current passport. Upload a clear copy of the photo page of your valid
                  passport. Ensure all text is legible. You do not need to upload entry- and exit-stamped pages. The
                  only exceptions are asylum seekers.
                </li>
                <li>
                  <strong>Residency Documents:</strong> You must upload this document because you said you're not a
                  United States citizen.
                  <ul>
                    <li> Upload a copy of your U.S. visa or U.S. Permanent Resident card.</li>
                    <li>
                      {' '}
                      If your card has expired, please provide a current Form I-94, Form I-797A, and/or Form I-20 that
                      specifies how long the expired card is extended, in addition to your expired visa or U.S.
                      Permanent Resident card.
                    </li>
                    <li>Canadian and Mexican citizens may instead upload the TN (temporary non-immigrant) visa. </li>
                    <li>
                      {' '}
                      Employment authorization cards (EAD), Social Security cards, or driver’s licenses are not
                      accepted. You may upload more than one document.
                    </li>
                  </ul>
                </li>
              </>
            )}
            <li>
              <strong>Legal and Other Documents:</strong> You can upload any other files you need for your application.
              This could include documents for name changes, court orders, or other legal papers. You may upload more
              than one document.
            </li>
          </ul>

          <h4 className='ds-u-margin-top--3'>File Requirements</h4>
          <p>
            Maximum file size: 4 MB per file. Supported formats vary by document type and are shown in each section.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default DocumentUploadPage;
